import { type ReactNode } from 'react'

import { ButtonRoot } from '@/components/common/Button'
import { FloatingHover } from '@/components/common/Floating'
import { ReadMoreLink } from '@/components/common/ReadMoreLink'
import { Icon } from '@/components/icons/Icon'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { type IconType } from '@/defs/icon'
import { ARTICLE_URLS } from '@/defs/misc'
import { cn } from '@/utils/commonUtil'

import styles from './FilterLabel.module.scss'

type Props = {
  variant: 'advanced' | 'bracket' | 'budget' | 'colorId' | 'preconCommander' | 'tag' | 'timeframe'
}

type ConfigType = {
  content?: ReactNode
  icon: IconType
  label: string
  link?: string
}

const CONFIG: Record<Props['variant'], ConfigType> = {
  advanced: {
    content: (
      <p>
        <strong>Advanced Filters</strong> filter the underlying dataset of decks. (Maximum 8)
      </p>
    ),
    icon: 'wrench',
    label: 'Advanced',
  },
  bracket: {
    content: (
      <p>
        The <strong>Commander Bracket</strong>
        {' system'} is a way for Commander players to ensure that they&apos;re sitting down for the kind of game that
        they want to play.
      </p>
    ),
    icon: 'bracketsCurly',
    label: 'Bracket',
    link: ARTICLE_URLS.BRACKETS_GUIDE,
  },
  budget: {
    icon: 'sackDollar',
    label: 'Budget',
  },
  colorId: {
    content: (
      <p>
        <strong>Color Identity</strong> filters for only decks with that exact color identity.
      </p>
    ),
    icon: 'mtg-wubrg',
    label: 'Color Identity',
  },
  preconCommander: {
    content: (
      <p>
        <strong>Precon Commander</strong> filters for precon decks with this set as a commander.
      </p>
    ),
    icon: 'crown',
    label: 'Precon Commander',
  },
  tag: {
    content: (
      <p>
        <strong>Tags</strong> are directly pulled from Archidekt tags and Moxfield hubs, and supplemented by
        EDHREC-detected themes.
      </p>
    ),
    icon: 'tag',
    label: 'Tag',
  },
  timeframe: {
    icon: 'hourglass',
    label: 'Timeframe',
  },
}

export const FilterLabel = ({ variant }: Props) => {
  const { content, icon, label, link } = CONFIG[variant]

  const contentElement = (
    <>
      {content}
      {link && (
        <p>
          <ReadMoreLink as='LinkOutbound' href={link} />
        </p>
      )}
    </>
  )

  return (
    <div className={cn(styles.container, { styles, variant })}>
      <IconWithLabel iconBefore={icon}>{label}</IconWithLabel>
      {content && (
        <FloatingHover content={contentElement} placement='right'>
          <ButtonRoot aria-label='Learn More' className={styles.infoIcon}>
            <Icon name='circleInfo' />
          </ButtonRoot>
        </FloatingHover>
      )}
    </div>
  )
}
